// Array variable parser/serializer entry.
// Example: parseArrayVariable("[1-3]") -> ["1","2","3"].
import { splitArrayItems } from './split'
import { expandRangeToken, hasMismatchedNumericRangeUnits } from './ranges'
// Strip wrapping quotes from resolved array items.
import { stripWrappingQuotes } from '../../loops/for-loop/selector'

// Detect array variable syntax by checking surrounding square brackets.
export function isArrayVariableValue(value: string): boolean {
  const trimmed = value.trim()
  // Return true when value uses bracketed array syntax.
  return trimmed.startsWith('[') && trimmed.endsWith(']')
}

// Parse array variable content into a normalized item list.
export function parseArrayVariable(value: string): string[] {
  const trimmed = value.trim()
  // Return empty when incoming value is not an array-like token.
  if (!isArrayVariableValue(trimmed)) {
    return []
  } else {
    // Continue parsing array inner content.
  }

  const inner = trimmed.slice(1, -1).trim()
  // Return empty when array body does not contain any item.
  if (!inner) {
    return []
  } else {
    // Continue splitting array items.
  }

  const items = splitArrayItems(inner)
  // Expand one-token range forms like `1-12` or `A-F`.
  if (items.length === 1) {
    const rangeValues = expandRangeToken(items[0])
    if (rangeValues) {
      return rangeValues
    } else {
      // Keep item list unchanged when token is not a range.
    }
  } else {
    // Return item list directly for non-single-token arrays.
  }

  // Return normalized array items with wrapping quotes stripped.
  return items.map((item) => stripWrappingQuotes(item))
}

// Detect mixed-unit range declaration in one-token array forms.
export function hasMismatchedArrayRangeUnits(value: string): boolean {
  const trimmed = value.trim()
  // Return false when incoming value is not an array-like token.
  if (!isArrayVariableValue(trimmed)) {
    return false
  } else {
    // Continue one-token array range checks.
  }

  const inner = trimmed.slice(1, -1).trim()
  // Return false when array body is empty.
  if (!inner) {
    return false
  } else {
    // Continue top-level item split for mismatch checks.
  }

  const items = splitArrayItems(inner)
  // Return false for non-range multi-item array declarations.
  if (items.length !== 1) {
    return false
  } else {
    // Return mismatch result for single-token range arrays.
    return hasMismatchedNumericRangeUnits(items[0])
  }
}

// Serialize normalized array items back into bracket syntax.
export function serializeArrayVariable(values: string[]): string {
  // Return array value string with comma-separated items.
  return `[${values.join(', ')}]`
}
